import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { settingValue } from '@/stores/settings.store';
import { useAuthStore } from '@/stores/auth.store';
import { otherLocale, useUiStore } from '@/i18n';
import { MESSAGES, translate, type MessageKey } from '@/i18n/messages';
import { useT } from '@/i18n/use-t';
import { SETTINGS, viewerOf, visibleSettings, type SettingsItem } from '@/lib/nav';
import { SETTINGS_PAGES, type SettingsPageId } from '@/lib/settings-pages';
import { DESCRIBED_KEYS } from '@/lib/settings-schema';
import { Glyph } from '@/components/shell';
import { ChevronRightIcon } from '@/components/icons';
import { Page, SaveBar, Section } from '@/components/page';
import { Alert, Button, Card, EmptyState, Skeleton, Switch, cx } from '@/components/ui';
import { OwnerOnly, SchemaSections, useSettings } from './schema';

/**
 * The settings area: its own menu beside its pages, the way the reference
 * dashboards keep setup out of the daily work's way (ADR-023's operate /
 * administer split). The home page is a grid of every group with a line on
 * what is behind it; on a phone that grid is the menu.
 */
export function SettingsLayout() {
  const user = useAuthStore((state) => state.user);
  const locale = useUiStore((state) => state.locale);
  const { pathname } = useLocation();
  const content = useRef<HTMLDivElement>(null);
  const groups = visibleSettings(viewerOf(user));

  // This area scrolls on its own on a desktop; a new page starts at its top.
  useEffect(() => {
    content.current?.scrollTo({ top: 0 });
  }, [pathname]);

  return (
    <div className="flex min-h-full lg:h-full">
      <nav
        aria-label={translate(SETTINGS.key, locale)}
        className="hidden w-60 shrink-0 flex-col gap-5 overflow-y-auto border-e border-line bg-surface/60 px-3 py-6 xl:flex"
      >
        <NavLink
          to={SETTINGS.to}
          end
          className={({ isActive }) =>
            cx(
              'rounded-control px-3 py-2 text-sm font-semibold transition-colors',
              isActive ? 'bg-brand-50 text-brand-700' : 'text-ink hover:bg-surface-sunken',
            )
          }
        >
          {translate('allSettings', locale)}
        </NavLink>
        {groups.map((group) => (
          <div key={group.id} className="flex flex-col gap-0.5">
            <p className="px-3 pb-1 text-[11px] font-semibold tracking-wide text-ink-faint uppercase">
              {translate(group.key, locale)}
            </p>
            {group.items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                title={translate(item.key, otherLocale(locale))}
                className={({ isActive }) =>
                  cx(
                    'flex items-center gap-2.5 rounded-control px-3 py-1.5 text-sm transition-colors',
                    isActive ? 'bg-brand-50 font-medium text-brand-700' : 'text-ink-muted hover:bg-surface-sunken hover:text-ink',
                  )
                }
              >
                <Glyph name={item.icon} className="size-4 shrink-0 opacity-80" />
                <span className="min-w-0 truncate">{translate(item.key, locale)}</span>
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      <div ref={content} className="min-w-0 flex-1 lg:overflow-y-auto">
        <Outlet />
      </div>
    </div>
  );
}

/** Every settings group, with a line on each page — the area's front door. */
export function SettingsHome() {
  const user = useAuthStore((state) => state.user);
  const locale = useUiStore((state) => state.locale);
  const t = useT();
  const groups = visibleSettings(viewerOf(user));

  return (
    <Page title={t('settings')} subtitle={t('settingsHomeSubtitle')} width="wide">
      <div className="flex flex-col gap-8">
        {groups.map((group) => (
          <section key={group.id}>
            <h2 className="mb-3 text-sm font-semibold text-ink-muted">{translate(group.key, locale)}</h2>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {group.items.map((item) => (
                <SettingsCard key={item.to} item={item} />
              ))}
            </div>
          </section>
        ))}
      </div>
    </Page>
  );
}

function SettingsCard({ item }: { item: SettingsItem }) {
  const locale = useUiStore((state) => state.locale);

  return (
    <Link
      to={item.to}
      className="group flex items-center gap-4 rounded-card border border-line bg-surface p-4 shadow-card transition-colors hover:border-brand-300 hover:bg-brand-50/40"
    >
      <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-control bg-surface-sunken text-ink-muted transition-colors group-hover:bg-brand-50 group-hover:text-brand-700">
        <Glyph name={item.icon} className="size-5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-ink">{translate(item.key, locale)}</span>
        <span className="mt-0.5 block text-xs text-ink-muted">{translate(item.hint, locale)}</span>
      </span>
      <ChevronRightIcon className="size-4 shrink-0 text-ink-faint rtl:-scale-x-100" />
    </Link>
  );
}

/** The header every settings page wears: its name, its line, and the way back to the rest. */
export function SettingsPage({
  title,
  subtitle,
  actions,
  tabs,
  fill,
  children,
}: {
  title: MessageKey;
  subtitle?: MessageKey;
  actions?: ReactNode;
  tabs?: ReactNode;
  fill?: boolean;
  children: ReactNode;
}) {
  const t = useT();
  return (
    <Page
      title={t(title)}
      subtitle={subtitle ? t(subtitle) : undefined}
      // The area's own menu is beside the page on a wide screen; below that,
      // the way back to it is a link.
      back={{ to: SETTINGS.to, label: t('allSettings'), wideHidden: true }}
      actions={actions}
      tabs={tabs}
      fill={fill}
      width="narrow"
    >
      {children}
    </Page>
  );
}

const PAGE_TITLES: Record<'general' | 'checkout' | 'customers' | 'catalog' | 'taxes' | 'customCode', { title: MessageKey; subtitle: MessageKey }> = {
  general: { title: 'navGeneral', subtitle: 'settingsGeneralHint' },
  checkout: { title: 'settingsCheckout', subtitle: 'settingsCheckoutHint' },
  customers: { title: 'settingsCustomers', subtitle: 'settingsCustomersHint' },
  catalog: { title: 'settingsCatalog', subtitle: 'settingsCatalogHint' },
  taxes: { title: 'settingsTaxes', subtitle: 'settingsTaxesHint' },
  customCode: { title: 'settingsCustomCode', subtitle: 'settingsCustomCodeHint' },
};

/** A settings page made only of described groups. */
export function SettingsSectionsPage({ page }: { page: keyof typeof PAGE_TITLES & SettingsPageId }) {
  const { title, subtitle } = PAGE_TITLES[page];
  return (
    <SettingsPage title={title} subtitle={subtitle}>
      <OwnerOnly>
        <SchemaSections keys={SETTINGS_PAGES[page]} />
      </OwnerOnly>
    </SettingsPage>
  );
}

/**
 * The store's optional features, each a switch. Flag names come from the API
 * and have no translation; showing the identifier is more honest than
 * inventing a label for each one.
 */
export function FeatureFlagsPage() {
  return (
    <SettingsPage title="featureFlags" subtitle="featureFlagsHint">
      <OwnerOnly>
        <FeatureFlags />
      </OwnerOnly>
    </SettingsPage>
  );
}

function FeatureFlags() {
  const { entries, loading, saving, error, save } = useSettings();
  const t = useT();
  const stored = (settingValue(entries, 'features.flags') ?? {}) as Record<string, boolean>;
  const [draft, setDraft] = useState<Record<string, boolean> | null>(null);
  const [justSaved, setJustSaved] = useState(false);
  const flags = draft ?? stored;
  const dirty = draft !== null && JSON.stringify(draft) !== JSON.stringify(stored);

  if (loading && entries.length === 0) return <Skeleton className="h-64" />;

  return (
    <>
      <Card>
        {Object.keys(flags).length === 0 ? (
          <EmptyState title={t('noFeatureFlags')} />
        ) : (
          <ul className="divide-y divide-line">
            {Object.entries(flags).map(([flag, enabled]) => {
              // A flag the admin has words for reads as a feature; one it does
              // not (a customer's extension's) keeps its key, so it can still be set.
              const name = MESSAGES[`flag_${flag}` as MessageKey] ? t(`flag_${flag}` as MessageKey) : null;
              return (
                <li key={flag} className="flex items-center justify-between gap-4 px-5 py-3.5">
                  <div className="min-w-0">
                    {name ? (
                      <>
                        <p className="text-sm font-medium text-ink">{name}</p>
                        <p className="text-xs text-ink-muted">{t(`flag_${flag}Hint` as MessageKey)}</p>
                      </>
                    ) : (
                      <span dir="ltr" className="font-mono text-sm text-ink">
                        {flag}
                      </span>
                    )}
                  </div>
                  <Switch
                    label={name ?? flag}
                    checked={enabled}
                    onChange={(next) => {
                      setJustSaved(false);
                      setDraft({ ...flags, [flag]: next });
                    }}
                  />
                </li>
              );
            })}
          </ul>
        )}
      </Card>
      <SaveBar
        saving={saving === 'features.flags'}
        dirty={dirty}
        message={error ? <Alert>{error}</Alert> : justSaved ? <span className="text-success">✓ {t('saved')}</span> : undefined}
        onSave={async () => {
          if (draft && (await save('features.flags', draft))) {
            setDraft(null);
            setJustSaved(true);
          }
        }}
        onDiscard={dirty ? () => setDraft(null) : undefined}
      />
    </>
  );
}

/**
 * Settings nothing has described yet. An operator should never need this, but
 * a setting added to the API must still be reachable the day it ships rather
 * than the day someone writes a form for it.
 */
export function AdvancedSettingsPage() {
  return (
    <SettingsPage title="advanced" subtitle="advancedHint">
      <OwnerOnly>
        <Advanced />
      </OwnerOnly>
    </SettingsPage>
  );
}

function Advanced() {
  const { entries, loading, saving, savedKey, error, save } = useSettings();
  const t = useT();
  const undescribed = entries.filter((entry) => !DESCRIBED_KEYS.includes(entry.key));

  if (loading && entries.length === 0) return <Skeleton className="h-64" />;
  if (undescribed.length === 0) {
    return (
      <Card className="flex flex-1 flex-col">
        <EmptyState title={t('advancedEmpty')} />
      </Card>
    );
  }

  return (
    <>
      {error ? <Alert>{error}</Alert> : null}
      {undescribed.map((entry) => (
        <Section key={entry.key} title={<span dir="ltr" className="font-mono">{entry.key}</span>}>
          <RawEditor
            value={entry.value}
            label={entry.key}
            saving={saving === entry.key}
            saved={savedKey === entry.key}
            onSave={(next) => void save(entry.key, next)}
          />
        </Section>
      ))}
    </>
  );
}

function RawEditor({
  value,
  label,
  saving,
  saved,
  onSave,
}: {
  value: unknown;
  label: string;
  saving: boolean;
  saved: boolean;
  onSave: (next: unknown) => void;
}) {
  const t = useT();
  const [text, setText] = useState(() => JSON.stringify(value, null, 2));
  const [invalid, setInvalid] = useState(false);

  return (
    <div className="flex flex-col gap-2">
      <textarea
        dir="ltr"
        value={text}
        onChange={(event) => {
          setText(event.target.value);
          setInvalid(false);
        }}
        rows={Math.min(16, text.split('\n').length + 1)}
        aria-label={label}
        className={cx(
          'w-full rounded-control border bg-surface p-3 font-mono text-xs text-ink',
          invalid ? 'border-danger' : 'border-line',
        )}
      />
      <div className="flex items-center justify-end gap-3">
        {invalid ? (
          <p role="alert" className="me-auto text-xs text-danger">
            {t('invalidJson')}
          </p>
        ) : null}
        {saved ? <span className="text-xs text-success">✓ {t('saved')}</span> : null}
        <Button
          variant="secondary"
          disabled={saving}
          onClick={() => {
            try {
              onSave(JSON.parse(text));
            } catch {
              // Caught here rather than sent: a malformed body comes back as a
              // generic validation error with no line to point at.
              setInvalid(true);
            }
          }}
        >
          {saving ? t('saving') : t('save')}
        </Button>
      </div>
    </div>
  );
}
